/**
 * Bring-your-own providers.
 *
 * Anthropic and OpenAI are reached with a key the Worker is started with —
 * ANTHROPIC_API_KEY and OPENAI_API_KEY. The key is never typed into the app or
 * stored in the database: `llm_providers.secret_name` only names the variable,
 * and the API reports nothing but whether it is present.
 *
 * The model list is read from the provider itself when you set it up, rather
 * than from a list baked into this repo, so what you see is what that company
 * currently offers. Whatever you save becomes selectable in the AI studio and on
 * the Prompts page like any other model.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, useApi } from '../lib/api'
import { EnumSelect } from './Select'
import { MODALITY_OPTIONS, MODALITY_HELP } from '../lib/modality'
import {
  Card, Field, Badge, Modal, Notice, Switch, Spinner, Empty, Loading, ErrorState,
  useToast, fmtInt, fmtDateTime,
} from './ui'

function CatalogModal({ provider, onClose, onSaved }) {
  const toast = useToast()
  const [chosen, setChosen] = useState(null)
  // Per model, not per batch: these vendors publish one model that both reads
  // pictures and writes text, and a single choice for the whole list meant every
  // model was filed under one of the two jobs it does. The server suggests which
  // is which; this is only where that suggestion can be overruled.
  const [modalities, setModalities] = useState({})
  const [busy, setBusy] = useState(false)
  const query = useApi(
    ['ai-catalog', provider?.id],
    `ai/providers/${provider?.id}/catalog`,
    { enabled: !!provider, retry: false, staleTime: 0, gcTime: 0 }
  )

  // Pre-tick whatever is already saved, the first time the list arrives.
  const models = query.data?.models ?? []
  const selected = chosen ?? new Set(models.filter((x) => x.saved).map((x) => x.model_key))
  const toggle = (key) => {
    const next = new Set(selected)
    next.has(key) ? next.delete(key) : next.add(key)
    setChosen(next)
  }
  const modalityOf = (mdl) => modalities[mdl.model_key] ?? mdl.modality ?? 'text'
  const setAll = (value) =>
    setModalities(Object.fromEntries(models.map((mdl) => [mdl.model_key, value])))

  const submit = async () => {
    setBusy(true)
    try {
      const res = await api(`ai/providers/${provider.id}/models`, {
        method: 'POST',
        body: {
          enable_provider: true,
          models: models
            .filter((x) => selected.has(x.model_key))
            .map((x) => ({ model_key: x.model_key, label: x.label, modality: modalityOf(x) })),
        },
      })
      toast.ok(`${provider.name}: ${fmtInt(res.count)} model(s) available`)
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={!!provider}
      onClose={onClose}
      size="lg"
      title={`${provider?.name ?? 'Provider'} — models available now`}
      footer={
        <>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !selected.size}>
            {busy ? <Spinner /> : `Use ${selected.size || 'no'} model${selected.size === 1 ? '' : 's'}`}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <span className="spacer" />
          <span className="small subtle">{fmtInt(models.length)} offered</span>
        </>
      }
    >
      {query.isLoading && <Loading label={`Asking ${provider?.name} what it offers…`} rows={3} />}
      {query.isError && (
        <div className="col">
          <ErrorState error={query.error} retry={query.refetch} />
          <p className="small subtle">
            This list comes straight from {provider?.name}, so a failure here usually means the key
            is missing, expired, or lacks permission to list models.
          </p>
        </div>
      )}
      {query.data && (
        <div className="col">
          <Notice icon="info">
            Read from {provider?.name} just now — this is their current line-up, not a list stored
            in this app. Tick the ones you want; anything you leave out never appears in a picker.
          </Notice>
          <Field
            label="What each model may be used for"
            hint="A model is offered only where it fits, so a text field can never call an image generator. “Both” is the usual answer for these providers — the same model reads a scanned page and rewrites a paragraph."
          >
            <div className="row">
              <span className="small subtle">Set every row to</span>
              {MODALITY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setAll(o.value)}
                  title={MODALITY_HELP[o.value]}
                >
                  {o.value}
                </button>
              ))}
            </div>
          </Field>
          {!models.length ? (
            <Empty title={`${provider?.name} returned no text models`} />
          ) : (
            <div className="table-wrap">
              <table className="data compact">
                <thead>
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Model</th>
                    <th>Used for</th>
                    <th>Released</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {models.map((mdl) => (
                    <tr key={mdl.model_key}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(mdl.model_key)}
                          onChange={() => toggle(mdl.model_key)}
                          aria-label={`Use ${mdl.label}`}
                        />
                      </td>
                      <td>
                        <div className="semibold">{mdl.label}</div>
                        <div className="tiny subtle mono">{mdl.model_key}</div>
                      </td>
                      <td style={{ minWidth: 170 }}>
                        <EnumSelect
                          value={modalityOf(mdl)}
                          onChange={(v) =>
                            setModalities((prev) => ({ ...prev, [mdl.model_key]: v || 'text' }))
                          }
                          options={MODALITY_OPTIONS.map((o) => ({ ...o, label: o.value }))}
                          isClearable={false}
                        />
                      </td>
                      <td className="small nowrap">
                        {mdl.created_at ? fmtDateTime(mdl.created_at) : <span className="subtle">—</span>}
                      </td>
                      <td>{mdl.saved ? <Badge tone="ok">already added</Badge> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function ProviderRow({ provider, canManage, onChanged, onOpenCatalog }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const toggle = async (on) => {
    setBusy(true)
    try {
      await api(`settings/llm/providers/${provider.id}`, { method: 'PUT', body: { is_enabled: on } })
      toast.ok(`${provider.name} ${on ? 'enabled' : 'disabled'}`)
      onChanged()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const enabled = !!provider.is_enabled
  const usable = enabled && provider.key_present && provider.models.some((x) => x.is_enabled)

  return (
    <div className="card">
      <div className="card-body">
        <div className="row" style={{ marginBottom: 6 }}>
          <h3>{provider.name}</h3>
          {provider.key_present ? (
            <Badge tone="ok" dot>
              key present
            </Badge>
          ) : (
            <Badge tone="warn" dot>
              key missing
            </Badge>
          )}
          {usable ? <Badge tone="accent">in use</Badge> : null}
          <span className="spacer" />
          <Switch
            checked={enabled}
            onChange={toggle}
            disabled={busy || !canManage}
            title={canManage ? 'Allow this provider to be used' : 'Only administrators can change this'}
          />
        </div>

        <dl className="kv">
          <dt>API key variable</dt>
          <dd className="mono small">{provider.secret_name || '—'}</dd>
          <dt>Endpoint</dt>
          <dd className="mono tiny">{provider.api_base || '—'}</dd>
          <dt>Models added</dt>
          <dd>
            {provider.models.length ? (
              <span className="chip-row">
                {provider.models.map((mdl) => (
                  <Badge key={mdl.id} tone={mdl.is_enabled ? undefined : 'warn'} title={mdl.model_key}>
                    {mdl.label} · {mdl.modality}
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="subtle">none yet</span>
            )}
          </dd>
        </dl>

        {!provider.key_present && (
          <Notice tone="warn" icon="warning" title={`${provider.secret_name} is not set`}>
            Supply it to the Worker and redeploy — <code>wrangler secret put {provider.secret_name}</code>{' '}
            for the live app, or a line in <code>.dev.vars</code> locally. Until then this provider
            cannot list its models or answer a prompt.
          </Notice>
        )}
        {provider.key_present && enabled && !provider.models.length && (
          <Notice icon="info">
            The key is there. Load the model list to choose which of {provider.name}&apos;s models this
            archive may use.
          </Notice>
        )}
      </div>
      <div className="card-foot">
        <button
          className="btn btn-sm btn-primary"
          onClick={() => onOpenCatalog(provider)}
          disabled={!canManage || !provider.key_present}
          title={provider.key_present ? undefined : 'Set the API key first'}
        >
          {provider.models.length ? 'Update model list' : 'Load current models'}
        </button>
        <span className="spacer" />
        <span className="tiny subtle">{provider.notes}</span>
      </div>
    </div>
  )
}

export default function ByoProviders() {
  const query = useApi('ai-providers', 'ai/providers')
  const [catalogFor, setCatalogFor] = useState(null)
  const qc = useQueryClient()

  const refresh = () => {
    query.refetch()
    // Every model dropdown — the studio's override, the Prompts editor — reads
    // the shared lookups payload, so it has to be re-fetched too.
    qc.invalidateQueries({ queryKey: ['lookups'] })
    qc.invalidateQueries({ queryKey: ['settings-llm'] })
    qc.invalidateQueries({ queryKey: ['ai-resolve'] })
  }

  if (query.isLoading) return null
  if (query.isError) return <ErrorState error={query.error} retry={query.refetch} />

  const d = query.data
  return (
    <Card
      title="Your Own Providers"
      sub="Use an Anthropic or OpenAI account instead of Cloudflare's models"
    >
      <div className="col">
        <Notice icon="key" title="Keys live on the Worker, not in this app">
          Each provider names an environment variable; the value is supplied when the application
          starts and is never typed in here, saved to the database, or sent back to the browser.
          Models you add become available on this page and on the Prompts page immediately.
        </Notice>

        {!d.canManage && (
          <Notice tone="warn" icon="lock">
            Your role can run these models but not change which are available. Ask an administrator
            to wire up a provider.
          </Notice>
        )}

        <div className="grid grid-2">
          {d.providers.map((p) => (
            <ProviderRow
              key={p.id}
              provider={p}
              canManage={d.canManage}
              onChanged={refresh}
              onOpenCatalog={setCatalogFor}
            />
          ))}
        </div>
      </div>

      {catalogFor && (
        <CatalogModal
          provider={catalogFor}
          onClose={() => setCatalogFor(null)}
          onSaved={refresh}
        />
      )}
    </Card>
  )
}
